import { FC } from 'react'
import { cn } from '@wellesley/ui'
import useMute from 'hooks/useMute'
import useBlock from 'hooks/useBlock'
import useFollow from 'hooks/useFollow'
import { AppsLink } from '@wellesley/router'
import { UserFull } from '@wellesley/openapi'
import FollowButton from 'components/FollowControl/FollowButton'
import { UserAvatar } from '@wellesley/user/components/UserAvatar'
import { useRelationship } from '@wellesley/user/hooks/relationship/useRelationship'
import useActionGuard from 'hooks/useActionGuard'
import replaceBrackets from 'helpers/replaceBrackets/index'
import { useSelector } from 'react-redux'
import addEmojisToText from 'helpers/addEmojisToText'
import { emojisSlice } from 'components/emojis/emojisSlice'
import { getUserImage } from 'helpers/getUserImage'
import { getDisplayUsername } from '@wellesley/user'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'

import UserNote from '../UserNote'
import StatusLabel from 'user/components/ProfilePage/StatusLabel'
import TwoLinesText from '../TwoLinesText'

import { Handlers } from './types'
import UserFollowers from '../UserFollowers'

import FollowersModal from '../FollowersModal'
import isBotProfile from 'helpers/ai/isBotProfile'
import BotBadge from 'components/BotBadge'

type TPopupContent = Handlers & {
  user: UserFull
  isOwnProfile: boolean
  className?: string
  isChannel?: boolean
  hideFollowButton?: boolean
}

const PopupContent: FC<TPopupContent> = ({
  user,
  className,
  isOwnProfile,
  isChannel,
  onFollow,
  onUnfollow,
  hideFollowButton = false,
}) => {
  const emojis = useSelector(emojisSlice.selectors.selectEmojis)

  const { follow, unfollow, renderGuestFollowModal } = useFollow({
    user,
    onFollow,
    onUnfollow,
  })

  const fullUsername = getUserNameWithDomain(user)
  const displayUsername = getDisplayUsername(fullUsername)

  const { relationship, isLoading } = useRelationship({
    userId: user.id,
  })

  const { isDisabled, ActionTooltipWrapper } = useActionGuard()

  const { isMuted } = useMute({
    user,
    relationship,
  })
  const [isBlocked] = useBlock({ user, relationship })

  const isBot = isBotProfile(user)

  return (
    <div
      className={cn(
        `p-m z-[6] w-[360px] rounded-lg bg-white text-base shadow-[0px_4px_6px_0px_var(--alpha-black-10)] outline outline-gray-200`,
        className,
      )}
      data-testid="infoPopupContent"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <div>
        {renderGuestFollowModal()}
        <div className="mb-s">
          <div className="flex justify-between">
            <div className="max-w-[calc(100%-80px)]">
              <AppsLink
                to="/@/:username"
                props={{ username: fullUsername }}
                className="block"
              >
                <div className="mb-xxxs gap-xxs flex items-center">
                  <h2
                    className="truncate text-lg font-semibold leading-none text-gray-900"
                    dangerouslySetInnerHTML={{
                      __html: addEmojisToText(user.displayName, emojis),
                    }}
                  />
                  <>
                    {isBlocked ? (
                      <StatusLabel status="blocked" iconOnly={true} />
                    ) : isMuted ? (
                      <StatusLabel status="muted" iconOnly={true} />
                    ) : isBot ? (
                      <BotBadge />
                    ) : null}
                  </>
                </div>
                <TwoLinesText>{displayUsername}</TwoLinesText>
              </AppsLink>
            </div>

            <UserAvatar
              className="ml-xxs shrink-0"
              size={48}
              name={user.displayName}
              imgUrl={getUserImage({ user })}
            />
          </div>
        </div>
        {user.summary && (
          <div
            data-testid="user-summary"
            className="my-xs line-clamp-2 overflow-hidden text-ellipsis leading-6 text-gray-900 empty:hidden"
            dangerouslySetInnerHTML={{
              __html: replaceBrackets(
                addEmojisToText(user.summary || '', emojis),
              ),
            }}
          />
        )}

        <div className="my-xs">
          <UserFollowers
            user={user}
            isChannel={isChannel}
            showEmpty={false}
            renderModal={({ user, tab, isOpen, onClose, ...modalProps }) => (
              <FollowersModal
                user={user}
                tab={tab}
                isOpen={isOpen}
                onClose={onClose}
                {...modalProps}
              />
            )}
          />
        </div>

        <UserNote
          userId={user.id}
          note={relationship.note as string}
          className="my-3"
        />

        {!isOwnProfile && !hideFollowButton && (
          <div className="mt-xs">
            <ActionTooltipWrapper triggerClassname="w-full">
              <FollowButton
                {...{ user, relationship }}
                follow={() => void follow()}
                unfollow={() => void unfollow()}
                disabled={isDisabled}
                followButtonText={isChannel ? 'Subscribe' : 'Follow'}
                isRelationshipPending={isLoading}
              />
            </ActionTooltipWrapper>
          </div>
        )}
      </div>
    </div>
  )
}

export default PopupContent
